import React, { useContext, useEffect } from 'react'
import { ModalActions } from '../../common/modal/Modal'
import { t } from '../../../i18n/i18n'
import { Button, Divider, makeStyles, Text, tokens } from '@fluentui/react-components'
import { useForm } from 'react-hook-form'
import { RiskAnalysis, RiskAnalysisActionPlanResponse, SWOT } from '../../../api/schemas/schema'
import { ControlledTextInput } from '../../common/inputs/TextInput'
import { GridItem } from '../../common/grid/GridItem'
import { Grid } from '../../common/grid/Grid'
import { api } from '../../../helpers/apiHelper'
import { DataContext } from '../../../context/DataContext'
import { handleMessage } from '../../../helpers/stateHelper'
import { SwotTypeBadge } from '../RiskBadges'
import { ControlledCheckbox } from '../../common/inputs/ControlledCheckbox'
import { ControlledMoneyInput } from '../../common/inputs/ControlledMoneyInput'
import { IItemState } from '../../../interfaces/IItemState'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { Form } from '../../common/form/Form'
import TextOutput from '../../common/inputs/TextOutput'

const useStyles = makeStyles({
   column: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXS,
   },
})

type ModalSWOTAnalyseEvaluateProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysis: RiskAnalysis
   open: boolean
   checkboxHasLocalState: boolean
   close: () => void
   toggleCheckbox: (swot: SWOT, openModal?: boolean) => SWOT
   selectedSwot: SWOT
   disabled?: boolean
   manualIsDirty: boolean
}

export const ModalSWOTAnalyseEvaluate = ({
   setRiskAnalysisState,
   analysis,
   open,
   checkboxHasLocalState,
   close,
   toggleCheckbox,
   selectedSwot,
   disabled,
   manualIsDirty,
}: ModalSWOTAnalyseEvaluateProps) => {
   const {
      control,
      reset,
      handleSubmit,
      watch,
      setValue,
      formState: { isDirty, isSubmitting },
   } = useForm<SWOT>()
   const dataContext = useContext(DataContext)
   const setRootState = dataContext.setRootState
   const { userAccount } = dataContext.state
   const classes = useStyles()

   useEffect(() => {
      if (selectedSwot) {
         reset(selectedSwot)
      }
   }, [selectedSwot, reset])

   const onSubmit = async (data: SWOT) => {
      try {
         data.modifiedBy = userAccount
         data.modifiedById = userAccount.id

         if (data.actionRequired === false) {
            data.proposedAction = null
            data.estimatedCost = null
         }

         const response: RiskAnalysisActionPlanResponse = (await api().createOrUpdateActionPlanForSwot(analysis.organisationId, analysis.id, data)).data

         const updatedRiskAnalysis = response.updatedAnalysis
         const updatedSwot = updatedRiskAnalysis.swoTs.find((s) => s.id === data.id)
         const createdActionPlan = response.actionPlan
         if (createdActionPlan == null) {
            updatedRiskAnalysis.actionPlans = updatedRiskAnalysis.actionPlans.filter((ap) => ap.swotId !== updatedSwot.id)
         }

         setRiskAnalysisState((prev) => ({
            ...prev,
            item: {
               ...prev.item,
               ...updatedRiskAnalysis,
            },
         }))

         close()
         reset(updatedSwot)
         handleMessage(setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('SWOT') }))
      } catch (error) {
         handleMessage(setRootState, 'error', t('CouldNotUpdateItem', { Item: t('SWOT') }))
      }
   }
   const onClose = () => {
      if (checkboxHasLocalState) {
         toggleCheckbox(selectedSwot)
      }
      reset()
      close()
   }

   return (
      <DraggableModal title={t('AnalyseEvaluate')} open={open} onClose={onClose}>
         <Form onSubmit={handleSubmit(onSubmit)}>
            <Grid>
               <GridItem size="12/12">
                  <TextOutput
                     label={t('Description')}
                     text={`${selectedSwot.order} ${t('Of')} ${analysis.swoTs.filter((s) => s.type === selectedSwot.type).length}`}
                  />
               </GridItem>
               <GridItem size="12/12">
                  <div className={classes.column}>
                     <Text size={400} weight="semibold">
                        {t('SWOT')}
                     </Text>
                     <SwotTypeBadge type={selectedSwot.type} />
                  </div>
               </GridItem>
               <GridItem size="12/12">
                  <TextOutput label={t('Title')} text={selectedSwot.title} />
               </GridItem>
               <GridItem size="12/12">
                  <TextOutput label={t('Description')} text={selectedSwot.description} />
               </GridItem>
               <GridItem size="12/12">
                  <Divider appearance="brand" />
               </GridItem>
               <GridItem size="12/12">
                  {watch('actionRequired') !== undefined && (
                     <ControlledCheckbox
                        onChange={(e, data) => {
                           setValue('actionRequired', Boolean(data.checked), { shouldDirty: true })
                           if (!data.checked) {
                              setValue('proposedAction', null)
                              setValue('estimatedCost', null)
                           }
                        }}
                        label={t('CreateProposedAction')}
                        control={control}
                        name={'actionRequired'}
                        checked={watch('actionRequired')}
                        disabled={disabled}
                     />
                  )}
               </GridItem>
               {watch('actionRequired') && (
                  <>
                     <GridItem size="12/12">
                        <ControlledTextInput control={control} name={'proposedAction'} label={t('ProposedAction')} multiline required disabled={disabled} />
                     </GridItem>
                     <GridItem size="12/12">
                        <ControlledMoneyInput control={control} name={'estimatedCost'} label={t('EstimatedCost')} disabled={disabled} />
                     </GridItem>
                  </>
               )}
               <GridItem size="12/12">
                  <ControlledTextInput control={control} name={'comment'} label={t('Comment')} multiline disabled={disabled} />
               </GridItem>
               <GridItem size="12/12">
                  <ModalActions>
                     <Button onClick={onClose} disabled={isSubmitting}>
                        {disabled ? t('Close') : t('Cancel')}
                     </Button>
                     <Button type="submit" appearance="primary" disabled={disabled || (!manualIsDirty && !isDirty) || isSubmitting}>
                        {t('Save')}
                     </Button>
                  </ModalActions>
               </GridItem>
            </Grid>
         </Form>
      </DraggableModal>
   )
}
